/**
 * SimpleAssistentPage - Einfacher Assistent (Simple Wizard)
 * 
 * 4-Schritte-Wizard für Endkunden zur einfachen Content-Erstellung.
 * Schritte: 1) Thema & Ziel, 2) Plattformen, 3) Stil, 4) Vorschau & Planung
 */

import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from 'sonner';
import { trackEvent } from "../lib/trackEvent";
import {
  Sparkles, Send, CheckCircle, Loader2, Eye,
  Target, Package, Edit, ArrowRight, Check, HelpCircle, Settings,
  CalendarDays
} from "lucide-react";
import BrandContextHint from '../components/BrandContextHint';
import BrandVisualHints from '../components/BrandVisualHints';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons mapping
const PlatformIcon = ({ platform, size = 18 }) => {
  switch (platform) {
    case 'facebook': return <span style={{fontSize: size}} className="font-bold">f</span>;
    case 'instagram': return <span style={{fontSize: size}} className="font-bold">IG</span>;
    case 'linkedin': return <span style={{fontSize: size}} className="font-bold">in</span>;
    case 'tiktok': return <span style={{fontSize: size}} className="font-bold">TT</span>;
    case 'google_business': return <span style={{fontSize: size}} className="font-bold">G</span>;
    default: return null;
  }
};

// ── Helper: Text in Zwischenablage kopieren ──────────────────────────────────
async function _copyText(text, onDone) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    onDone?.();
  } catch { /* silent */ }
}

// ── Amber-Box mit Copy-Button ─────────────────────────────────────────────────
const ImagePromptHintBox = ({ hint }) => {
  const [copied, setCopied] = useState(false);
  if (!hint) return null;

  const handleCopy = async () => {
    await _copyText(hint, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex items-start gap-2 px-3 py-3 bg-amber-50 border border-amber-200 rounded-lg">
      <span className="text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true">🖼️</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <p className="text-xs font-medium text-amber-700">Bild- &amp; Thumbnail-Tipp</p>
          <button
            onClick={handleCopy}
            className={`text-xs px-2 py-0.5 rounded flex-shrink-0 transition-colors ${
              copied
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700 hover:bg-amber-200"
            }`}
          >
            {copied ? "✓ Kopiert" : "Bild-Prompt kopieren"}
          </button>
        </div>
        <p className="text-xs text-amber-600 leading-snug">{hint}</p>
        <p className="text-xs text-amber-500 mt-1 leading-snug">
          Nutze diesen Tipp für KI-Bilder, Thumbnails oder als Briefing für Designer.
        </p>
      </div>
    </div>
  );
};

const SimpleAssistentPage = () => {
  // Wizard State
  const [step, setStep] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  
  // Step 1: Ziel & Thema
  const [goal, setGoal] = useState('visibility');
  const [topic, setTopic] = useState('');
  
  // Step 2: Plattformen
  const [channels, setChannels] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  
  // Step 3: Stil & Variante
  const [tone, setTone] = useState('locker');
  const [postType, setPostType] = useState('short');
  
  // Step 4: Posts (generiert) & Scheduling
  const [posts, setPosts] = useState({});
  const [activePreview, setActivePreview] = useState(null);
  const [useSmartScheduling, setUseSmartScheduling] = useState(true);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('10:00');
  
  // Result
  const [result, setResult] = useState(null);
  const [brandContextUsed, setBrandContextUsed] = useState(false);
  const [brandVisualHints, setBrandVisualHints] = useState(null);
  const [imagePromptHint, setImagePromptHint] = useState(null);

  // Tutorial State
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  
  // Tutorial Schritte
  const tutorialSteps = [
    {
      title: "Willkommen!",
      text: "In 60 Sekunden zeigen wir dir, wie du Beiträge erstellst. Ganz einfach!",
      target: null,
      position: "center"
    },
    {
      title: "Schritt 1: Dein Thema",
      text: "Schreibe hier kurz, worum es gehen soll. Zum Beispiel: 'AVGS-Coaching für Bewerber'",
      target: "topic-input",
      position: "bottom"
    },
    {
      title: "Schritt 2: Wähle Kanäle",
      text: "Wir posten automatisch auf allen verbundenen Kanälen. Du kannst einzelne abwählen.",
      target: "step1-next",
      position: "top"
    },
    {
      title: "Schritt 3: Stil wählen",
      text: "Wähle, wie dein Beitrag klingen soll: locker, professionell oder motivierend.",
      target: null,
      position: "center"
    },
    {
      title: "Fertig!",
      text: "Am Ende klickst du auf 'Beiträge planen' - wir kümmern uns um den Rest!",
      target: null,
      position: "center"
    }
  ];
  
  // Check if tutorial should be shown.
  // Nicht automatisch aufpoppen, wenn der Nutzer das globale Onboarding bereits durchlaufen hat
  // (sonst zwei „Willkommen"-Overlays direkt hintereinander). Über den Hilfe-Button bleibt es erreichbar.
  useEffect(() => {
    const tutorialSeen = localStorage.getItem('foerderfuchs_tutorial_seen');
    const onboardingDone = localStorage.getItem('ff_onboarding_done') === '1';
    if (!tutorialSeen && !onboardingDone) {
      const timer = setTimeout(() => setShowTutorial(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const completeTutorial = () => {
    localStorage.setItem('foerderfuchs_tutorial_seen', 'true');
    setShowTutorial(false);
    setTutorialStep(0);
  };
  
  const skipTutorial = () => {
    localStorage.setItem('foerderfuchs_tutorial_seen', 'true');
    setShowTutorial(false);
  };
  
  const nextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      completeTutorial();
    }
  };
  
  const restartTutorial = () => {
    setTutorialStep(0);
    setShowTutorial(true);
  };

  // Load channels on mount
  useEffect(() => {
    const loadChannels = async () => {
      try {
        const response = await axios.get(`${API}/simple-flow/channels`);
        const chs = response.data?.channels || [];
        setAvailableChannels(chs);
        setChannels(chs.map(c => c.id));
      } catch (error) {
        const defaultChs = ['instagram', 'facebook', 'linkedin', 'tiktok', 'google_business'];
        setAvailableChannels(defaultChs.map(id => ({ id, name: id, connected: false })));
        setChannels(defaultChs);
      }
    };
    loadChannels();
  }, []);

  // Goals
  const goals = [
    { id: 'visibility', label: 'Mehr Sichtbarkeit', desc: 'Reichweite aufbauen', icon: Eye, color: 'from-blue-500 to-cyan-500' },
    { id: 'leads', label: 'Mehr Anfragen/Leads', desc: 'Neue Kunden gewinnen', icon: Target, color: 'from-green-500 to-emerald-500' },
    { id: 'product', label: 'Angebot vorstellen', desc: 'Produkt oder Service zeigen', icon: Package, color: 'from-purple-500 to-pink-500' },
    { id: 'other', label: 'Sonstiges', desc: 'Allgemeiner Beitrag', icon: Edit, color: 'from-orange-500 to-amber-500' }
  ];

  // Tones
  const tones = [
    { id: 'locker', label: 'Locker', emoji: '😊' },
    { id: 'professionell', label: 'Professionell', emoji: '💼' },
    { id: 'motivierend', label: 'Motivierend', emoji: '🚀' },
    { id: 'seriös', label: 'Seriös', emoji: '🎯' }
  ];

  // Post Types
  const postTypes = [
    { id: 'short', label: 'Kurz-Post', desc: 'Schnell & kompakt' },
    { id: 'carousel', label: 'Bild/Carousel-Post', desc: 'Mit Bildern' },
    { id: 'video', label: 'Video/Reel', desc: 'Bewegtbild' }
  ];

  const toggleChannel = (channelId) => {
    setChannels(prev => 
      prev.includes(channelId)
        ? prev.filter(c => c !== channelId)
        : [...prev, channelId]
    );
  };

  const generatePosts = async () => {
    if (!topic.trim()) {
      toast.error('Bitte beschreibe dein Thema');
      return;
    }
    if (channels.length === 0) {
      toast.error('Wähle mindestens einen Kanal');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/simple-flow/generate`, {
        goal,
        topic: topic.trim(),
        channels,
        tone,
        post_type: postType
      });
      
      setPosts(response.data.posts || {});
      setBrandContextUsed(response.data.brand_context_used === true);
      setBrandVisualHints(response.data.brand_visual_hints || null);
      setImagePromptHint(response.data.image_prompt_hint || null);
      setActivePreview(channels[0]);
      setStep(4);
      toast.success('Beiträge wurden erstellt!');
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const updatePost = (channel, newText) => {
    setPosts(prev => ({
      ...prev,
      [channel]: { ...prev[channel], text: newText }
    }));
  };

  const schedulePosts = async () => {
    setScheduling(true);
    try {
      const customDateTime = !useSmartScheduling && customDate 
        ? `${customDate}T${customTime}:00`
        : null;

      const response = await axios.post(`${API}/simple-flow/schedule`, {
        posts,
        use_smart_scheduling: useSmartScheduling,
        custom_datetime: customDateTime
      });
      
      setResult(response.data);
      setStep(5);
      // Funnel: Aktivierung (Wizard durchlaufen) — mit Kanal-Status, um echte vs. Entwurf-Aktivierung zu trennen.
      trackEvent("wizard_completed", {
        scheduled_count: response.data?.scheduled_count ?? 0,
        any_channel_connected: availableChannels.some((c) => c.connected),
      });
      toast.success(`${response.data.scheduled_count} Posts wurden geplant!`);
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setScheduling(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setGoal('visibility');
    setTopic('');
    setTone('locker');
    setPostType('short');
    setPosts({});
    setResult(null);
    setBrandContextUsed(false);
    setBrandVisualHints(null);
    setImagePromptHint(null);
    setUseSmartScheduling(true);
    setCustomDate('');
    setCustomTime('10:00');
  };

  const getPlatformColor = (platform) => {
    const colors = {
      instagram: 'bg-gradient-to-r from-purple-500 to-pink-500',
      facebook: 'bg-blue-600',
      linkedin: 'bg-sky-700',
      tiktok: 'bg-gray-900',
      google_business: 'bg-emerald-600'
    };
    return colors[platform] || 'bg-gray-500';
  };

  return (
    <div data-testid="simple-assistent-page" className="max-w-3xl mx-auto animate-fade-in">
      {/* Tutorial Overlay */}
      {showTutorial && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div data-testid="tutorial-card" className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in relative">
              <div className="flex justify-center gap-2 mb-6">
                {tutorialSteps.map((_, idx) => (
                  <div key={idx} className={`w-2 h-2 rounded-full transition-all ${idx === tutorialStep ? 'bg-fox-fire w-6' : 'bg-stone-200'}`} />
                ))}
              </div>
              
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-fox-fire to-orange-400 flex items-center justify-center">
                  {tutorialStep === 0 && <Sparkles className="text-white" size={32} />}
                  {tutorialStep === 1 && <Edit className="text-white" size={32} />}
                  {tutorialStep === 2 && <CheckCircle className="text-white" size={32} />}
                  {tutorialStep === 3 && <Sparkles className="text-white" size={32} />}
                  {tutorialStep === 4 && <Send className="text-white" size={32} />}
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-center text-stone-900 mb-2">{tutorialSteps[tutorialStep].title}</h3>
              <p className="text-center text-stone-600 mb-6">{tutorialSteps[tutorialStep].text}</p>
              
              <div className="flex gap-3">
                <button data-testid="tutorial-skip" onClick={skipTutorial} className="flex-1 px-4 py-3 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-xl transition-colors">
                  Überspringen
                </button>
                <button data-testid="tutorial-next" onClick={nextTutorialStep} className="flex-1 px-4 py-3 bg-fox-fire text-white rounded-xl hover:bg-fox-fire/90 transition-colors font-medium flex items-center justify-center gap-2">
                  {tutorialStep === tutorialSteps.length - 1 ? <>Los geht's!</> : <>Weiter<ArrowRight size={18} /></>}
                </button>
              </div>
              
              <p className="text-center text-xs text-stone-400 mt-4">{tutorialStep + 1} von {tutorialSteps.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-8 relative">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-fox-fire/10 to-orange-100 rounded-full mb-4">
          <Sparkles className="text-fox-fire" size={18} />
          <span className="text-sm font-medium text-fox-fire">Einfacher Assistent</span>
        </div>
        <button data-testid="tutorial-help-btn" onClick={restartTutorial} className="absolute top-0 right-0 p-2 text-stone-400 hover:text-fox-fire hover:bg-stone-100 rounded-lg transition-colors" title="Tutorial erneut starten">
          <HelpCircle size={20} />
        </button>
      </div>

      {/* Progress Bar */}
      {step < 5 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step >= s ? 'bg-fox-fire text-white' : 'bg-stone-200 text-stone-500'}`}>
                  {step > s ? <Check size={16} /> : s}
                </div>
                {s < 4 && <div className={`w-16 sm:w-24 h-1 mx-2 rounded ${step > s ? 'bg-fox-fire' : 'bg-stone-200'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-stone-500">
            <span>Thema</span>
            <span>Kanäle</span>
            <span>Stil</span>
            <span>Fertig</span>
          </div>
        </div>
      )}

      {/* Step 1: Ziel & Thema */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mb-2">Was möchtest du posten?</h1>
            <p className="text-stone-500">Schreibe 1-2 Sätze zu deinem Thema. Den Rest übernimmt die KI.</p>
          </div>

          <div className="bg-white rounded-2xl border-2 border-stone-200 p-6 focus-within:border-fox-fire transition-colors">
            <label className="block text-sm font-medium text-stone-700 mb-2">Dein Thema</label>
            <textarea data-testid="topic-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="z. B. AVGS-Bewerbungscoaching für junge Erwachsene" className="w-full h-32 p-0 border-0 focus:ring-0 resize-none text-lg placeholder:text-stone-400" autoFocus />
            <div className="flex justify-between items-center pt-3 border-t border-stone-100">
              <span className="text-xs text-stone-400">{topic.length} Zeichen</span>
              <span className="text-xs text-stone-400">Tipp: Je konkreter, desto besser</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-3">Ziel des Posts</label>
            <div className="grid grid-cols-2 gap-3">
              {goals.map((g) => {
                const Icon = g.icon;
                const isSelected = goal === g.id;
                return (
                  <button key={g.id} data-testid={`goal-${g.id}`} onClick={() => setGoal(g.id)} className={`p-4 rounded-xl border-2 transition-all text-left ${isSelected ? 'border-fox-fire bg-fox-fire/5' : 'border-stone-200 hover:border-stone-300 bg-white'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${g.color} flex items-center justify-center`}>
                        <Icon className="text-white" size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{g.label}</p>
                        <p className="text-xs text-stone-500">{g.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <button data-testid="step1-next" onClick={() => setStep(2)} disabled={!topic.trim()} className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            Weiter
            <ArrowRight size={20} />
          </button>
          
          <p className="text-center text-xs text-stone-400">Wir führen dich Schritt für Schritt durch.</p>
        </div>
      )}

      {/* Step 2: Plattformen */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mb-2">Wo soll dein Beitrag erscheinen?</h1>
            <p className="text-stone-500">Standardmäßig posten wir auf allen verbundenen Kanälen. Du kannst einzelne abwählen.</p>
          </div>

          {availableChannels.length > 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-stone-600">{channels.length} von {availableChannels.length} Kanälen aktiv</span>
                <button onClick={() => setChannels(channels.length === availableChannels.length ? [] : availableChannels.map(c => c.id))} className="text-sm text-fox-fire hover:underline">
                  {channels.length === availableChannels.length ? 'Alle abwählen' : 'Alle auswählen'}
                </button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availableChannels.map((ch) => {
                  const isSelected = channels.includes(ch.id);
                  return (
                    <button key={ch.id} data-testid={`channel-${ch.id}`} onClick={() => toggleChannel(ch.id)} className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${isSelected ? 'border-fox-fire bg-fox-fire/5' : 'border-stone-200 bg-white hover:border-stone-300'}`}>
                      <div className={`w-10 h-10 rounded-lg ${getPlatformColor(ch.id)} flex items-center justify-center flex-shrink-0`}>
                        <PlatformIcon platform={ch.id} size={20} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium capitalize text-stone-900">{ch.name || ch.id}</p>
                        {ch.connected ? (
                          <p className="text-xs text-green-600">✓ Verbunden{ch.profile ? ` (${ch.profile})` : ''}</p>
                        ) : ch.status === 'reconnect_needed' ? (
                          <p className="text-xs text-amber-600">Neu verbinden erforderlich</p>
                        ) : (
                          <p className="text-xs text-stone-400">Noch nicht verbunden</p>
                        )}
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-fox-fire bg-fox-fire' : 'border-stone-300'}`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700"><strong>Tipp:</strong> Wir empfehlen, alle Kanäle aktiv zu lassen, damit möglichst viele Menschen dich sehen.</p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6 text-center">
              <p className="text-amber-800 mb-4">Noch keine Konten verbunden. Bitte verbinde zuerst deine Social-Media-Konten.</p>
              <NavLink to="/settings" className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600">
                <Settings size={18} />
                Konten verbinden
              </NavLink>
            </div>
          )}

          <div className="flex gap-4">
            <button onClick={() => setStep(1)} className="px-6 py-4 border border-stone-200 rounded-xl hover:bg-stone-50 font-medium">Zurück</button>
            <button data-testid="step2-next" onClick={() => setStep(3)} disabled={channels.length === 0} className="flex-1 btn-primary py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50">
              Weiter
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Stil & Variante */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mb-2">Wie soll dein Beitrag klingen?</h1>
            <p className="text-stone-500">Wähle einen Stil, der zu dir passt. Du kannst den Text später noch ändern.</p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <label className="block text-sm font-medium text-stone-700 mb-3">Tonalität</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {tones.map((t) => (
                <button key={t.id} data-testid={`tone-${t.id}`} onClick={() => setTone(t.id)} className={`p-4 rounded-xl border-2 transition-all text-center ${tone === t.id ? 'border-fox-fire bg-fox-fire/5' : 'border-stone-200 hover:border-stone-300 bg-white'}`}>
                  <span className="text-2xl mb-1 block">{t.emoji}</span>
                  <span className="font-medium text-sm">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <label className="block text-sm font-medium text-stone-700 mb-3">Post-Art</label>
            <div className="grid grid-cols-3 gap-3">
              {postTypes.map((type) => (
                <button key={type.id} data-testid={`posttype-${type.id}`} onClick={() => setPostType(type.id)} className={`p-4 rounded-xl border-2 transition-all text-center ${postType === type.id ? 'border-fox-fire bg-fox-fire/5' : 'border-stone-200 hover:border-stone-300 bg-white'}`}>
                  <p className="font-medium">{type.label}</p>
                  <p className="text-xs text-stone-500">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-stone-50 rounded-xl p-4">
            <p className="text-sm text-stone-600">
              <strong>Zusammenfassung:</strong> {channels.length} Beiträge für {channels.map(c => availableChannels.find(a => a.id === c)?.name || c).join(', ')}, Stil: {tones.find(t => t.id === tone)?.label}
            </p>
          </div>

          <div className="flex gap-4">
            <button onClick={() => setStep(2)} className="px-6 py-4 border border-stone-200 rounded-xl hover:bg-stone-50 font-medium">Zurück</button>
            <button data-testid="step3-generate" onClick={generatePosts} disabled={generating} className="flex-1 btn-primary py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50">
              {generating ? <><Loader2 className="animate-spin" size={20} />Beiträge werden erstellt...</> : <><Sparkles size={20} />Weiter</>}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Vorschau & Planung */}
      {step === 4 && (
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mb-2">Fast fertig - prüfe deine Beiträge</h1>
            <p className="text-stone-500">Wir haben Vorschläge für deine Kanäle erstellt. Du kannst sie bei Bedarf anpassen.</p>
          </div>

          <BrandContextHint show={brandContextUsed} />

          <BrandVisualHints hints={brandVisualHints} />

          {imagePromptHint && (
            <ImagePromptHintBox hint={imagePromptHint} />
          )}

          <div className="flex gap-2 overflow-x-auto pb-2">
            {Object.keys(posts).map((platform) => (
              <button key={platform} data-testid={`preview-tab-${platform}`} onClick={() => setActivePreview(platform)} className={`flex items-center gap-2 px-4 py-2.5 rounded-full whitespace-nowrap transition-all ${activePreview === platform ? 'bg-fox-fire text-white shadow-md' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>
                <PlatformIcon platform={platform} size={16} />
                <span className="capitalize font-medium">{platform}</span>
              </button>
            ))}
          </div>

          {activePreview && posts[activePreview] && (
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
              <div className={`p-4 ${getPlatformColor(activePreview)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PlatformIcon platform={activePreview} size={24} className="text-white" />
                    <div className="text-white">
                      <h3 className="font-bold capitalize">{activePreview}</h3>
                      <p className="text-xs opacity-80">{posts[activePreview].text?.length || 0} Zeichen</p>
                    </div>
                  </div>
                  {(() => {
                    const ch = availableChannels.find(c => c.id === activePreview);
                    return ch?.connected ? (
                      <span className="px-2 py-1 bg-white/20 rounded-full text-xs text-white font-medium">✓ Live</span>
                    ) : (
                      <span className="px-2 py-1 bg-white/20 rounded-full text-xs text-white/80">Testbetrieb</span>
                    );
                  })()}
                </div>
              </div>
              
              <div className="p-4">
                <textarea data-testid={`post-editor-${activePreview}`} value={posts[activePreview].text || ''} onChange={(e) => updatePost(activePreview, e.target.value)} className="w-full h-40 p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-fox-fire/20 focus:border-fox-fire resize-none text-sm" />
                {posts[activePreview].hashtags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {posts[activePreview].hashtags.map((tag, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-full">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-bold text-stone-900 mb-4">Wann sollen wir posten?</h3>
            
            <div className="space-y-3">
              <label data-testid="smart-scheduling-option" className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${useSmartScheduling ? 'border-fox-fire bg-fox-fire/5' : 'border-stone-200 hover:border-stone-300'}`}>
                <input type="radio" name="scheduling" checked={useSmartScheduling} onChange={() => setUseSmartScheduling(true)} className="sr-only" />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${useSmartScheduling ? 'border-fox-fire bg-fox-fire' : 'border-stone-300'}`}>
                  {useSmartScheduling && <Check size={12} className="text-white" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-900">Automatisch beste Zeiten nutzen</p>
                  <p className="text-xs text-stone-500">Empfohlen - unser KI-Scheduler wählt optimale Zeiten</p>
                </div>
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Empfohlen</span>
              </label>

              <label data-testid="manual-scheduling-option" className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${!useSmartScheduling ? 'border-fox-fire bg-fox-fire/5' : 'border-stone-200 hover:border-stone-300'}`}>
                <input type="radio" name="scheduling" checked={!useSmartScheduling} onChange={() => setUseSmartScheduling(false)} className="sr-only" />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!useSmartScheduling ? 'border-fox-fire bg-fox-fire' : 'border-stone-300'}`}>
                  {!useSmartScheduling && <Check size={12} className="text-white" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-900">Ich wähle Datum und Uhrzeit selbst</p>
                  <p className="text-xs text-stone-500">Du bestimmst, wann gepostet wird</p>
                </div>
              </label>
            </div>

            {!useSmartScheduling && (
              <div className="mt-4 grid grid-cols-2 gap-4 p-4 bg-stone-50 rounded-xl">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Datum</label>
                  <input data-testid="custom-date" type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-fox-fire/20 focus:border-fox-fire text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Uhrzeit</label>
                  <input data-testid="custom-time" type="time" value={customTime} onChange={(e) => setCustomTime(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-fox-fire/20 focus:border-fox-fire text-sm" />
                </div>
              </div>
            )}
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-green-900">Bereit zum Planen</h3>
                <p className="text-sm text-green-700">Wir planen <strong>{Object.keys(posts).length} Beiträge</strong> auf <strong>{Object.keys(posts).length} Kanälen</strong>.</p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <button onClick={() => setStep(3)} className="px-6 py-4 border border-stone-200 rounded-xl hover:bg-stone-50 font-medium">Zurück</button>
            <button data-testid="schedule-btn" onClick={schedulePosts} disabled={scheduling || (!useSmartScheduling && !customDate)} className="flex-1 btn-primary py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-50">
              {scheduling ? <><Loader2 className="animate-spin" size={20} />Wird geplant...</> : <><Send size={20} />Beiträge planen</>}
            </button>
          </div>
          
          <p className="text-center text-xs text-stone-400">Wenn alles gut aussieht, klick auf "Beiträge planen". Wir kümmern uns um den Rest.</p>
        </div>
      )}

      {/* Step 5: Success */}
      {step === 5 && result && (
        <div className="text-center space-y-6 py-6">
          {/* Erfolgs-Icon */}
          <div className="relative inline-flex">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-green-500" size={48} />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 bg-fox-fire rounded-full flex items-center justify-center shadow-lg">
              <Sparkles className="text-white" size={14} />
            </div>
          </div>

          {/* Headline — ehrlich an Kanal-Status gekoppelt: ohne verbundenen Kanal wird (noch) nichts veröffentlicht */}
          <div>
            <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2">
              {availableChannels.some(c => c.connected) ? "Du bist startklar!" : "Fast geschafft!"}
            </h1>
            <p className="text-xl text-green-700 font-medium">{result.scheduled_count} Beiträge wurden eingeplant</p>
          </div>

          {/* Kernnachricht */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-200 p-5 text-left">
            {availableChannels.some(c => c.connected) ? (
              <p className="text-stone-700 text-center leading-relaxed">
                Ab jetzt laufen deine Posts automatisch. Änderungen kannst du jederzeit im{' '}
                <NavLink to="/monthly-plan" className="text-fox-fire font-semibold hover:underline">
                  Monatsplan
                </NavLink>{' '}
                vornehmen.
              </p>
            ) : (
              <p className="text-stone-700 text-center leading-relaxed">
                Deine Beiträge sind als <strong>Entwurf</strong> geplant. Damit Förderfuchs sie
                automatisch für dich veröffentlicht,{' '}
                <NavLink to="/kanale" className="text-fox-fire font-semibold hover:underline">
                  verbinde noch einen Kanal
                </NavLink>.
              </p>
            )}
          </div>

          {/* Geplante Posts */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5 text-left">
            <h3 className="font-bold text-stone-900 mb-3">Eingeplante Beiträge:</h3>
            <div className="space-y-2">
              {result.posts?.map((post, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                  <div className={`w-10 h-10 rounded-lg ${getPlatformColor(post.platform)} flex items-center justify-center`}>
                    <PlatformIcon platform={post.platform} size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium capitalize">{post.platform.replace('_', ' ')}</p>
                    <p className="text-sm text-stone-500">
                      {new Date(post.scheduled_date).toLocaleDateString('de-DE')} um {post.scheduled_time} Uhr
                    </p>
                  </div>
                  <CheckCircle className="text-green-500 ml-auto" size={18} />
                </div>
              ))}
            </div>
          </div>

          {/* Nächste Schritte */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5 text-left">
            <h3 className="font-bold text-stone-900 mb-3">Was als nächstes?</h3>
            <div className="space-y-3">
              <NavLink to="/monthly-plan" className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl hover:bg-orange-50 transition-colors group">
                <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200">
                  <CalendarDays className="text-orange-500" size={18} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-800">Monatsplan ansehen</p>
                  <p className="text-xs text-stone-500">Alle geplanten Posts im Überblick</p>
                </div>
                <ArrowRight size={16} className="text-stone-400 group-hover:text-orange-500" />
              </NavLink>

              <NavLink to="/kanale" className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl hover:bg-blue-50 transition-colors group">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200">
                  <Settings className="text-blue-500" size={18} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-800">Kanäle verbinden</p>
                  <p className="text-xs text-stone-500">Damit deine Beiträge wirklich veröffentlicht werden</p>
                </div>
                <ArrowRight size={16} className="text-stone-400 group-hover:text-blue-500" />
              </NavLink>

              <NavLink to="/hilfe" className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl hover:bg-purple-50 transition-colors group">
                <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200">
                  <HelpCircle className="text-purple-500" size={18} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-800">Hilfe & Anleitung</p>
                  <p className="text-xs text-stone-500">Schritt-für-Schritt-Anleitung</p>
                </div>
                <ArrowRight size={16} className="text-stone-400 group-hover:text-purple-500" />
              </NavLink>
            </div>
          </div>

          {/* CTA */}
          <button
            data-testid="new-post-btn"
            onClick={resetWizard}
            className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2"
          >
            <Sparkles size={20} />
            Weiteren Post erstellen
          </button>
        </div>
      )}
    </div>
  );
};

export default SimpleAssistentPage;
